import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Remix Icons
import { 
    RiBriefcase4Fill, 
    RiLockPasswordLine, 
    RiMailLine, 
    RiPhoneLine, 
    RiShieldUserLine, 
    RiUserFollowLine, 
    RiErrorWarningLine, 
    RiArrowRightLine,
    RiEyeLine,
    RiEyeOffLine
} from 'react-icons/ri';

export default function Login() {
    const [tab, setTab] = useState('staff'); // 'staff' or 'applicant'
    const [loginInput, setLoginInput] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { loginStaff, loginApplicant } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (tab === 'staff') {
                const res = await loginStaff(loginInput, password);
                if (res.success) {
                    const role = res.user.role;
                    if (role === 'admin') navigate('/admin');
                    else if (role === 'coordinator') navigate('/users');
                    else if (role === 'company_hr') navigate('/hr-stall');
                    else navigate('/');
                }
            } else {
                const res = await loginApplicant(loginInput, password);
                if (res.success) {
                    navigate('/applicant-portal');
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 relative overflow-hidden">
            {/* Ambient Background Lights */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-80 sm:w-96 h-80 sm:h-96 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none animate-pulse-glow"></div>
            <div className="absolute bottom-10 right-10 w-64 sm:w-80 h-64 sm:h-80 bg-purple-600/15 rounded-full blur-3xl pointer-events-none"></div>

            <div className="w-full max-w-md glass-card rounded-3xl p-6 sm:p-8 shadow-2xl border relative z-10">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-500 shadow-xl shadow-indigo-500/30 mb-3">
                        <RiBriefcase4Fill className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-2xl font-black bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                        Welcome to Avsar (अवसर)
                    </h1>
                    <p className="text-xs text-slate-400 mt-1 font-medium">Mega Job Fair Portal • Sign in to continue</p>
                </div>

                {/* Tabs */}
                <div className="flex bg-slate-900/50 p-1.5 rounded-2xl mb-6 border border-slate-800/80">
                    <button
                        type="button"
                        onClick={() => { setTab('staff'); setError(''); }}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-black transition-all flex items-center justify-center space-x-1.5 ${
                            tab === 'staff'
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/30'
                                : 'text-slate-400 hover:text-indigo-400'
                        }`}
                    >
                        <RiShieldUserLine className="w-4 h-4 shrink-0" />
                        <span>Staff Login</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => { setTab('applicant'); setError(''); }}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-black transition-all flex items-center justify-center space-x-1.5 ${
                            tab === 'applicant'
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/30'
                                : 'text-slate-400 hover:text-emerald-400'
                        }`}
                    >
                        <RiUserFollowLine className="w-4 h-4 shrink-0" />
                        <span>Applicant Login</span>
                    </button>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="mb-4 p-3.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold flex items-center space-x-2">
                        <RiErrorWarningLine className="w-4 h-4 shrink-0 text-red-500" />
                        <span>{error}</span>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-300 mb-1.5">
                            {tab === 'staff' ? 'Email or Mobile Number' : 'Email or Phone Number'}
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                {tab === 'staff' ? <RiMailLine className="w-4 h-4" /> : <RiPhoneLine className="w-4 h-4" />}
                            </div>
                            <input
                                type="text"
                                value={loginInput}
                                onChange={(e) => setLoginInput(e.target.value)}
                                required
                                placeholder={tab === 'staff' ? 'Enter email or mobile number' : 'Enter email or phone number'}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl glass-input text-sm font-medium"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-300 mb-1.5">Password</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                <RiLockPasswordLine className="w-4 h-4" />
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="••••••••"
                                className="w-full pl-10 pr-10 py-2.5 rounded-xl glass-input text-sm font-medium"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <RiEyeOffLine className="w-4 h-4 text-indigo-400" /> : <RiEyeLine className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3.5 rounded-xl font-black text-sm text-white shadow-xl transition-all flex items-center justify-center space-x-2 ${
                            tab === 'staff'
                                ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:brightness-110 shadow-indigo-600/30'
                                : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 shadow-emerald-600/30'
                        }`}
                    >
                        {loading ? (
                            <span>Authenticating...</span>
                        ) : (
                            <>
                                <span>Sign In as {tab === 'staff' ? 'Staff' : 'Applicant'}</span>
                                <RiArrowRightLine className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>

                <div className="mt-6 pt-4 border-t border-slate-800/80 text-center">
                    <Link to="/public-register" className="text-xs text-indigo-400 hover:text-indigo-300 font-bold transition-colors">
                        Need to register as a Candidate or Company? Click here
                    </Link>
                </div>
            </div>
        </div>
    );
}
